'use client';

import { ComponentType, useEffect, useState } from 'react';
import { Experience, ExperienceConfiguration } from '@ninetailed/experience.js-react';
import { ComponentTypeEnum } from '@ninetailed/experience.js-shared';
import { PageSectionProps } from 'components/PageSection/types';
import { ModuleProps, ModuleTypeName } from 'utils/modules';
import { ContentfulModule } from 'libs/contentful/types';

export type NinetailedContentfulModule = ContentfulModule & {
  id: string;
};

// `PageSectionProps & ModuleProps[ModuleTypeName]` is required (not just the
// union) because the PageSection live-preview path in `NinetailedExperience`
// passes a `ComponentType<PageSectionProps>` here; the intersection narrows
// the union so the contravariant `ComponentType` slot accepts it.
interface ClientNinetailedExperienceProps {
  id: string;
  passthroughProps: PageSectionProps & ModuleProps[ModuleTypeName];
  module: ComponentType<PageSectionProps & ModuleProps[ModuleTypeName]>;
  experiences: ExperienceConfiguration<NinetailedContentfulModule>[];
  hasExperiments: boolean;
}

interface HiddenVariant {
  hidden?: boolean;
}

const hasHiddenFlag = (variant: unknown): variant is HiddenVariant => {
  return typeof variant === 'object' && variant !== null && 'hidden' in variant;
};

const isVisibleVariant = <VariantType,>(variant: VariantType) => {
  if (hasHiddenFlag(variant)) {
    return !variant.hidden;
  }

  return true;
};

/**
 * Renders a client Ninetailed experience for a given component
 * https://www.contentful.com/developers/docs/personalization/experience-sdk/#render-experiences
 * @param id - The id of the component
 * @param passthroughProps - The passthrough props to render
 * @param component - The component to render
 * @param experiences - The experiences to render
 * @param hasExperiments - Whether the page has experiments
 * @returns The rendered component
 */
export const ClientNinetailedExperience = ({
  id,
  passthroughProps,
  module: Module,
  experiences,
  hasExperiments,
}: ClientNinetailedExperienceProps) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Small delay to ensure preview plugin is ready
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  if (!hasExperiments) {
    return <Module {...(passthroughProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName])} />;
  }

  // Handle the variant data within the experiences (GraphQL data doesn't need flattening)
  // Filter out hidden variants to prevent Ninetailed from selecting variants with no data
  const processedExperiences = experiences.map(experience => {
    const processedExperience = {
      ...experience,
      components: experience.components?.map(component => {
        if (component.type !== ComponentTypeEnum.EntryReplacement) {
          return component;
        }

        return {
          ...component,
          variants: component.variants?.filter(isVisibleVariant),
        };
      }),
    };

    if ('variants' in processedExperience) {
      const { variants } = processedExperience;
      if (Array.isArray(variants)) {
        processedExperience.variants = variants.filter(isVisibleVariant);
      }
    }

    return processedExperience;
  });

  // Show baseline until ready
  if (!isReady) {
    return <Module {...(passthroughProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName])} />;
  }

  return (
    <Experience
      {...(passthroughProps as unknown as PageSectionProps & ModuleProps[ModuleTypeName])}
      id={id}
      component={Module}
      experiences={processedExperiences}
    />
  );
};
